import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { apiFetch } from '../lib/api';
import type { AuditEntryWithUser, PaginatedResponse } from '@nasabtree/shared-types';

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function actionIcon(action: string) {
  switch (action) {
    case 'insert':
      return <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">+</span>;
    case 'update':
      return <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs">
        <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3"><path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.146 9.146H3v-3l9.146-9.146Z" /></svg>
      </span>;
    case 'delete':
      return <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">&minus;</span>;
    default:
      return null;
  }
}

function entryDescription(entry: AuditEntryWithUser, t: (key: string) => string): string {
  const name =
    (entry.action === 'delete' ? entry.old_data?.ism_ar : entry.new_data?.ism_ar) as string | undefined;
  const nameStr = name ? `: ${name}` : '';

  const table = entry.table_name;
  const action = entry.action;

  const key = `audit.${action}${table.charAt(0).toUpperCase() + table.slice(1).replace('_', '')}`;
  const translated = t(key);
  if (translated !== key) return translated + nameStr;

  // Fallback
  return `${action} ${table}${nameStr}`;
}

export function AuditLogPanel({ treeId, open, onClose }: { treeId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AuditEntryWithUser[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableFilter, setTableFilter] = useState('');
  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (tableFilter) params.set('table_name', tableFilter);
    const res = await apiFetch<PaginatedResponse<AuditEntryWithUser>>(
      `/api/trees/${treeId}/audit?${params.toString()}`,
    );
    if (res.ok) {
      setEntries(res.data.items);
      setTotal(res.data.total);
    }
    setLoading(false);
  }, [treeId, page, tableFilter]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-2xl z-50 max-h-[85vh] flex flex-col">
          <div className="p-6 pb-3 border-b border-gray-100">
            <Dialog.Title className="text-lg font-bold text-gray-900">
              {t('audit.title')}
            </Dialog.Title>
            <div className="mt-3 flex gap-2">
              {['', 'persons', 'marriages', 'parent_child'].map((f) => (
                <button
                  key={f}
                  onClick={() => { setTableFilter(f); setPage(1); }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    tableFilter === f
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {f === '' ? t('audit.filterAll') : t(`audit.filter_${f}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 pt-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : entries.length === 0 ? (
              <p className="text-center text-gray-400 py-12 text-sm">{t('audit.empty')}</p>
            ) : (
              <div className="space-y-3">
                {entries.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-3 py-2">
                    {actionIcon(entry.action)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {entryDescription(entry, t)}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {entry.changed_by_email && (
                          <span className="text-gray-500">{entry.changed_by_email} · </span>
                        )}
                        {relativeTime(entry.changed_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
              <span>{total} {t('audit.entries')}</span>
              <div className="flex items-center gap-1.5">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-2.5 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium"
                >
                  ‹
                </button>
                <span className="px-2 text-sm">{page}/{totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2.5 py-1 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium"
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <Dialog.Close asChild>
            <button className="absolute top-4 end-4 text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
